import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import AuthShell, { AuthDivider } from '../components/AuthShell';

export default function Register() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const googleBtnRef = useRef();

  useEffect(() => {
    api.get('/auth/config')
      .then(r => setGoogleEnabled(r.data.google_enabled !== false))
      .catch(() => setGoogleEnabled(false));
  }, []);

  useEffect(() => {
    if (!googleEnabled || !window.google || !process.env.REACT_APP_GOOGLE_CLIENT_ID || !googleBtnRef.current) return;
    window.google.accounts.id.initialize({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      callback: handleGoogleCredential,
    });
    window.google.accounts.id.renderButton(googleBtnRef.current, {
      theme: 'outline',
      size: 'large',
      width: googleBtnRef.current?.offsetWidth || 360,
      text: 'signup_with',
    });
  }, [googleEnabled]);

  async function handleGoogleCredential({ credential }) {
    setLoading(true);
    try {
      const r = await api.post('/auth/google', { credential });
      login(r.data.token, r.data.user);
      toast.success('Account ready! Next, connect your business WhatsApp number in Settings.');
      nav('/settings');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Google sign-in failed');
    }
    setLoading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error('Passwords do not match');
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      const r = await api.post('/auth/register', { name: form.name, email: form.email, password: form.password });
      login(r.data.token, r.data.user);
      toast.success('Account created! Next, connect your business WhatsApp number in Settings.');
      nav('/settings');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed');
    }
    setLoading(false);
  }

  return (
    <AuthShell
      eyebrow="Start growing"
      title="Create your workspace"
      subtitle="Bring WhatsApp, email and your customer relationships into one place."
      compact
    >
        {googleEnabled && process.env.REACT_APP_GOOGLE_CLIENT_ID && (
          <>
            <div ref={googleBtnRef} className="auth-google" />
            <AuthDivider>or register with email</AuthDivider>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group auth-field">
            <label className="label">Your name</label>
            <input className="input" required autoFocus value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Tendai Moyo" />
          </div>
          <div className="form-group auth-field">
            <label className="label">Email address</label>
            <input className="input" type="email" required value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="you@example.com" />
          </div>
          <div className="form-group auth-field">
            <label className="label">Password</label>
            <input className="input" type="password" required value={form.password}
              aria-describedby="pw-help"
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" />
            {/* Requirements belong under the field, not in the placeholder —
                a placeholder disappears the moment you start typing. */}
            <span className="field-help" id="pw-help">At least 6 characters.</span>
          </div>
          <div className="form-group auth-field">
            <label className="label">Confirm password</label>
            <input className="input" type="password" required value={form.confirm}
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} placeholder="••••••••" />
          </div>
          <button type="submit" className={'btn btn-primary btn-lg btn-block' + (loading ? ' is-loading' : '')} disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <p className="auth-switch">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
    </AuthShell>
  );
}
